import type { ReactNode } from 'react';
import s from './crmDock.module.css';

/** Mảnh dựng chung cho bảng phải khổ 1920 của vùng CRM (theo code board: section / donut / thanh / việc / thông báo). */
export const Sec = ({ title, icon, children }: { title: string; icon?: ReactNode; children: ReactNode }) => (
  <section className={s.sec}>
    <h2 className={s.h2}>
      {icon}
      {title}
    </h2>
    {children}
  </section>
);

export interface Slice { k: string; v: number; c: string }

/** Donut 132×132 (r=52, nét 16) như CHX.donut của board. */
export function Donut({ slices, total, unit, aria }: { slices: Slice[]; total: number; unit: string; aria: string }) {
  const sum = slices.reduce((a, d) => a + d.v, 0) || 1;
  const C = 2 * Math.PI * 52;
  let acc = 0;
  return (
    <div className={s.donutRow}>
      <svg role="img" aria-label={aria} width="110" height="110" viewBox="0 0 132 132" style={{ flexShrink: 0 }}>
        <circle cx="66" cy="66" r="52" fill="none" stroke="var(--line)" strokeWidth="16" />
        {slices.map((d) => {
          const L = (d.v / sum) * C;
          const g = Math.max(0, L - 3);
          const el = <circle key={d.k} cx="66" cy="66" r="52" fill="none" stroke={d.c} strokeWidth="16" strokeDasharray={`${g.toFixed(1)} ${(C - g).toFixed(1)}`} strokeDashoffset={(-acc).toFixed(1)} transform="rotate(-90 66 66)" />;
          acc += L;
          return el;
        })}
        <text x="66" y="68" textAnchor="middle" fontSize="22" fontWeight="550" fill="var(--tx)" fontFamily="inherit">{total}</text>
        <text x="66" y="86" textAnchor="middle" fontSize="12" fill="var(--lb)" fontFamily="inherit">{unit}</text>
      </svg>
      <div className={s.legend}>
        {slices.map((d) => (
          <span key={d.k}>
            <i style={{ background: d.c }} />
            <em>{d.k}</em>
            <b>{d.v}</b>
          </span>
        ))}
      </div>
    </div>
  );
}

export const Kpis = ({ items }: { items: Array<{ k: string; v: string; tone?: 'err' | 'warn' }> }) => (
  <div className={s.kpis}>
    {items.map((k) => (
      <div key={k.k}>
        <span>{k.k}</span>
        <b style={{ color: k.tone ? `var(--${k.tone})` : undefined }}>{k.v}</b>
      </div>
    ))}
  </div>
);

/** Hàng thanh ngang: nhãn · thanh · số. */
export const Bars = ({ rows, label, cols, h, warn }: { rows: Array<{ k: string; n: number; text?: string; c?: string }>; label: (r: { k: string; n: number }) => string; cols: string; h: number; warn?: boolean }) => {
  const mx = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div>
      {rows.map((r) => (
        <div key={r.k} role="img" aria-label={label(r)} className={s.bar} style={{ gridTemplateColumns: cols, height: h }}>
          <span>{r.k}</span>
          <div className={s.track}>
            <div className={`${s.fill} ${warn ? s.fillWarn : ''}`} style={{ width: `${Math.round((r.n / mx) * 100)}%`, background: r.c }} />
          </div>
          {warn ? <b style={{ color: 'var(--warn)', fontSize: 12 }}>{r.text}</b> : <b>{r.n}</b>}
        </div>
      ))}
    </div>
  );
};

export function Columns({ rows, aria }: { rows: Array<{ k: string; n: number }>; aria: string }) {
  const mx = Math.max(1, ...rows.map((r) => r.n));
  return (
    <>
      <div role="img" aria-label={aria} className={s.cols}>
        {rows.map((c) => (
          <div key={c.k} className={s.col}>
            <b>{c.n}</b>
            <div title={`${c.k}: ${c.n} khách`} style={{ height: `${c.n ? Math.max(3, Math.round((c.n / mx) * 100)) : 0}%` }} />
          </div>
        ))}
      </div>
      <div className={s.colLb}>
        {rows.map((c) => (
          <span key={c.k}>{c.k}</span>
        ))}
      </div>
    </>
  );
}

export interface DockTask { code: string; text: string; due: string; tone?: 'err' | 'warn' }
export const Tasks = ({ items }: { items: DockTask[] }) => (
  <>
    {items.map((k) => (
      <div key={k.text} className={s.task}>
        <span className={s.code}>{k.code}</span>
        <div style={{ minWidth: 0 }}>
          <b>{k.text}</b>
          <div className={s.due} style={{ color: k.tone ? `var(--${k.tone})` : 'var(--lb)' }}>{k.due}</div>
        </div>
      </div>
    ))}
  </>
);

export interface DockNoti { text: string; when: string; tone: 'blue' | 'red' | 'amber' | 'gray'; unread?: boolean; go?: () => void }
const DOT = { blue: 'var(--d-blue)', red: 'var(--err-bd)', amber: 'var(--d-amber)', gray: 'var(--d-gray)' } as const;
export const Notis = ({ items }: { items: DockNoti[] }) => (
  <>
    {items.map((n) => {
      const body = (
        <>
          <span className={s.dot} style={{ background: DOT[n.tone] }} />
          <div>
            <div style={{ fontWeight: n.unread ? 600 : 450 }}>{n.text}</div>
            <div className={s.when}>{n.when}</div>
          </div>
        </>
      );
      return n.go ? (
        <button key={n.text} type="button" className={s.noti} onClick={n.go}>{body}</button>
      ) : (
        <div key={n.text} className={s.noti}>{body}</div>
      );
    })}
  </>
);

export interface DockAlert { n: number; k: string; d: string; tone: 'err' | 'warn'; go?: () => void }
/** Thẻ cảnh báo: số lớn · tiêu đề · mô tả (tab Cảnh báo của Mint-Contract). */
export const Alerts = ({ items }: { items: DockAlert[] }) => (
  <>
    {items.map((a) => (
      <button key={a.k} type="button" className={s.alert} onClick={a.go}>
        <b style={{ color: `var(--${a.tone})` }}>{a.n}</b>
        <span>
          <strong>{a.k}</strong>
          <small>{a.d}</small>
        </span>
      </button>
    ))}
  </>
);
